import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/design-system';
import { formatMoney, type Order, type OrderAccess } from '@/entities/order';
import { formatDayTimeShort } from '@/shared/lib';

import { inspectionsCopy } from '../copy';
import { OrderStatusPill } from './OrderStatusPill';
import { OrderSurveyAction } from './SurveyActionButton';

export interface OrderTableProps {
    orders: readonly Order[];
    selectedUuid: string | null;
    access: OrderAccess;
    onSelect: (order: Order) => void;
}

/**
 * De werklijst. Zeven vaste kolommen met een `min-width` van 940 px: bij een
 * smaller venster schuift de tabel horizontaal binnen haar kaart, ze wordt
 * nooit samengeperst en nooit geclipt (principe 11).
 *
 * De prijs staat er alleen voor wie geld mag zien; voor een inspecteur stuurt
 * de API hem niet mee en staat er een streepje.
 *
 * De laatste kolom is de ene handeling van die keuring — starten, hervatten of het
 * rapport openen — zodat je hem niet eerst hoeft te selecteren. Onder 640 px is er geen
 * tabel maar een stapel kaarten ({@link OrderCards}).
 */
export function OrderTable({ orders, selectedUuid, access, onSelect }: OrderTableProps) {
    return (
        <Table
            columns="84px 132px 118px minmax(0,1fr) 150px 120px 96px 212px"
            minWidth={1152}
            label={inspectionsCopy.tableLabel}
        >
            <TableHead>
                <TableHeaderCell>{inspectionsCopy.columnId}</TableHeaderCell>
                <TableHeaderCell>{inspectionsCopy.columnStatus}</TableHeaderCell>
                <TableHeaderCell>{inspectionsCopy.columnDate}</TableHeaderCell>
                <TableHeaderCell>{inspectionsCopy.columnAddress}</TableHeaderCell>
                <TableHeaderCell>{inspectionsCopy.columnService}</TableHeaderCell>
                <TableHeaderCell>{inspectionsCopy.columnInspector}</TableHeaderCell>
                <TableHeaderCell align="end">{inspectionsCopy.columnPrice}</TableHeaderCell>
                <TableHeaderCell>{inspectionsCopy.columnAction}</TableHeaderCell>
            </TableHead>

            <TableBody>
                {orders.map((order) => (
                    <TableRow
                        key={order.uuid}
                        selected={order.uuid === selectedUuid}
                        onSelect={() => onSelect(order)}
                    >
                        <TableCell mono muted>
                            {order.number}
                        </TableCell>
                        <TableCell className="py-[9px]">
                            <OrderStatusPill order={order} />
                        </TableCell>
                        <TableCell muted className="text-[12px]">
                            {order.scheduledAt
                                ? formatDayTimeShort(order.scheduledAt)
                                : inspectionsCopy.noDate}
                        </TableCell>
                        <TableCell emphasis truncate>
                            {order.property.shortAddress}
                        </TableCell>
                        <TableCell muted truncate className="text-[12px]">
                            {order.serviceLabel}
                        </TableCell>
                        <TableCell muted truncate className="text-[12px]">
                            {order.assignee?.name ?? inspectionsCopy.noInspector}
                        </TableCell>
                        <TableCell mono align="end">
                            {formatMoney(order.financials?.price ?? null)}
                        </TableCell>
                        <TableCell className="py-[7px]">
                            <OrderSurveyAction
                                order={order}
                                access={access}
                                size="sm"
                                fullWidth
                                quiet
                            />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
